import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { AdvertiseLayout } from '@/components/advertise/AdvertiseLayout';
import { AdvertiseSideSummary } from '@/components/advertise/AdvertiseSideSummary';
import { AdvertiseFooterNav } from '@/components/advertise/AdvertiseFooterNav';
import { useAdvertise } from '@/store/useAdvertise';
import { useStepGuard, sellersStepValid } from '@/utils/advertiseGuards';
import { SELLERS_STEPS } from '@/config/advertiseSteps';
import { createBoostCheckout } from '@/services/advertiseApi';
import { formatPrice } from '@/utils/format';
import { toast } from 'sonner';
import { ADVERTISE_SELLERS } from '@/constants/testIds/advertise';

function isSafeCheckoutUrl(url) {
  if (typeof url !== 'string' || !url) return false;
  if (url.startsWith('/') && !url.startsWith('//')) return true;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && (u.hostname === 'checkout.stripe.com' || u.hostname.endsWith('.stripe.com'));
  } catch {
    return false;
  }
}

export default function SellersCheckout() {
  const navigate = useNavigate();
  const sellers = useAdvertise((s) => s.sellers);
  useStepGuard(sellersStepValid.checkout(sellers), '/advertise/sellers');
  const [paying, setPaying] = useState(false);

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await createBoostCheckout({
        quoteId: sellers.quoteId,
        sellers: { firstName: sellers.firstName, lastName: sellers.lastName, email: sellers.email, phone: sellers.phone, address: sellers.address, role: sellers.role },
      });
      const url = res?.checkout_url;
      if (!isSafeCheckoutUrl(url)) throw new Error('Invalid checkout URL');
      if (/^https:\/\//i.test(url)) {
        window.location.href = url;
        return;
      }
      navigate(url);
    } catch (e) {
      toast.error('Payment could not be started. Please try again.');
      setPaying(false);
    }
  };

  return (
    <AdvertiseLayout
      trackLabel="Listing Boost"
      steps={SELLERS_STEPS}
      currentStepId="checkout"
      title="Payment"
      subtitle="Secure, one-time charge — your boost activates immediately after payment."
      sidebar={<AdvertiseSideSummary title="Order summary" items={[{ label: 'Listing Boost', value: formatPrice(sellers.quoteAmount) }, { label: 'Billed to', value: sellers.email }]} />}
      footer={
        <AdvertiseFooterNav
          onBack={() => navigate('/advertise/sellers/review')}
          onNext={handlePay}
          loading={paying}
          nextLabel={`Pay ${formatPrice(sellers.quoteAmount)}`}
          nextTestId={ADVERTISE_SELLERS.checkoutSubmit}
        />
      }
    >
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        <Lock size={16} className="shrink-0 text-blue-600" />
        Payments are processed securely. You will not be charged until you confirm below.
      </div>
      <p className="mt-4 text-sm text-slate-500">Card details are collected on the next secure step.</p>
    </AdvertiseLayout>
  );
}
